import { FC } from 'react';
import { useListFirmBankDetails } from 'hooks/data/firm/useFirmBankDetails';
import { Api } from 'further-types/firm';
import { firmBankDetailsDisplay } from 'further-ui/utils';
import BasicSelect from 'further-ui/components/BasicSelect';

type Props = {
  firmId: string;
  currentValue?: string;
  onChange: (bankDetailsId: string, bankDetails?: Api.FirmBankDetail) => void;
  placeholder: string;
  allowDeselection?: boolean;
  deselectionLabel?: string;
  allowCashBalanceSelection?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  refetchOnWindowFocus?: boolean;
};

export const CashBalanceOptionValue = 'CASH_BALANCE';

export const FirmBankDetailsSelector: FC<Props> = ({
  firmId,
  currentValue,
  onChange,
  placeholder,
  allowDeselection,
  deselectionLabel = 'None',
  allowCashBalanceSelection,
  refetchOnWindowFocus,
  disabled,
  ...rest
}) => {
  const firmBankDetails = useListFirmBankDetails(firmId, refetchOnWindowFocus);

  const hasNoOptions =
    firmBankDetails.data?.length === 0 && !allowCashBalanceSelection;

  const options = [
    ...(firmBankDetails.data ?? []).map((detail) => ({
      value: detail._id,
      label: firmBankDetailsDisplay(detail),
    })),
    ...(allowCashBalanceSelection
      ? [{ value: CashBalanceOptionValue, label: 'Cash balance payment' }]
      : []),
  ];

  const computedPlaceholder = firmBankDetails.isLoading
    ? 'Loading...'
    : hasNoOptions
    ? 'No bank details found'
    : placeholder;

  return (
    <BasicSelect
      fullWidth
      size="small"
      value={currentValue}
      placeholder={computedPlaceholder}
      nullOptionLabel={allowDeselection ? deselectionLabel : undefined}
      options={options}
      disabled={disabled || firmBankDetails.isLoading || hasNoOptions}
      onChange={(value) => {
        if (!value) {
          onChange('', undefined);
          return;
        }
        const bankDetails = firmBankDetails.data?.find(
          (item) => item._id === value,
        );
        onChange(value, bankDetails);
      }}
      {...rest}
    />
  );
};
